import React, { ComponentPropsWithoutRef } from 'react';
import { Text } from '../Text/Text';
import { Box } from '../Box/Box';
import { Icons, SettingToggle, StyledToggle, Switch, ToggleSize } from './Toggle.styles';
import { MarginProps } from '../../../theme/styledProps';
import { extractMarginProps } from '../utils/extractMarginProps';
import { HelperText } from '../HelperText/HelperText';

export interface ToggleProps extends Omit<ComponentPropsWithoutRef<'input'>, 'size'>, MarginProps {
  children?: string;
  helperText?: React.ReactNode;
  size?: ToggleSize;
  variant?: 'form' | 'setting';
}

export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  ({ children, helperText, size = 'large', variant = 'form', disabled, style, ...rest }, ref) => {
    const Component = variant === 'setting' ? SettingToggle : StyledToggle;

    const alignItems =
      children && helperText && (variant === 'setting' || size === 'small')
        ? 'flex-start'
        : 'center';

    return (
      <Box display="flex" alignItems="center" p="2" style={style} {...extractMarginProps(rest)}>
        <Component as={children ? 'label' : 'div'} disabled={disabled} style={{ alignItems }}>
          <input ref={ref} type="checkbox" disabled={disabled} {...rest} />
          <Switch variant={size}>
            <Icons variant={size} />
          </Switch>

          {!!children && (
            <>
              <Text
                fontWeight={500}
                color={disabled ? 'neutral.500' : 'neutral.700'}
                ml={variant === 'form' ? '8' : -2}
                mr={variant === 'setting' ? '8' : 0}
                mt={alignItems === 'flex-start' ? -2 : 0}
              >
                {children}

                {helperText && (
                  <HelperText css={{ maxWidth: variant === 'setting' ? 260 : 'unset' }}>
                    {helperText}
                  </HelperText>
                )}
              </Text>
            </>
          )}
        </Component>
      </Box>
    );
  },
);
